import React, { useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { TopicScore } from 'lincd-irlcg/lib/shapes/TopicScore';
import { Server } from 'lincd-server-utils/lib/utils/Server';
import { packageName } from '../../package';
import { withRetry } from 'lincd-irlcg/lib/utils/helper';
import { Spinner } from '../atoms/Spinner';
import Counter from '../atoms/Counter';
import style from '../../pages/LiveGameboard.scss.json';

interface GameBoardStatsProps {
  eventUri: string;
}

const GameBoardStats = ({ eventUri }: GameBoardStatsProps) => {
  const { t } = useTranslate();
  let prefix = 'liveGameboard';

  const [location, setLocation] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  const getTotals = async () => {
    setLoading(true);
    try {
      const result = await withRetry(
        () => TopicScore.getEventTotals(eventUri),
        10,
        1000 * 60,
      );
      setTotals(result);
    } catch (error) {
      console.warn(`error loading gameboard totals:`, error);
      alert(
        t(
          prefix + '.loadingError',
          'Hmm, things are a little crowded right now. We were not able to load the game board. Please try again in a moment.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const getLocation = async () => {
    setLoading(true);
    try {
      const response = await withRetry(
        () => Server.call(packageName, 'getTotalLocation', eventUri),
        10,
        1000 * 60,
      );
      setLocation(response);
    } catch (error) {
      console.warn(`error loading location totals:`, error);
      alert(
        t(
          prefix + '.loadingError',
          'Hmm, things are a little crowded right now. We were not able to load the location totals. Please try again in a moment.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTotals();
    getLocation();

    // setInterval to refresh totals every 6 seconds
    const interval = setInterval(() => {
      getTotals();
      getLocation();
    }, 1000 * 60);

    return () => clearInterval(interval);
  }, [eventUri]);

  return (
    <div className={style.statusContainer}>
      <div className={style.status}>
        <h2 className={style.title}>
          <Counter value={totals?.totalPlayer || 0} />
        </h2>
        <p>{t('word.Players', 'Players')}</p>
      </div>
      <div className={style.status}>
        <h2 className={style.title}>
          <Counter value={totals?.totalActions || 0} />
        </h2>
        <p>{t('word.peaceActions', 'Peace Actions')}</p>
      </div>
      <div className={style.status}>
        <h2 className={style.title}>
          <Counter value={location?.city || 0} />
        </h2>
        <p>{t('word.cities', 'Cities')}</p>
      </div>
      <div className={style.status}>
        <h2 className={style.title}>
          <Counter value={location?.country || 0} />
        </h2>
        <p>{t('word.countries', 'Countries')}</p>
      </div>
    </div>
  );
};

export default GameBoardStats;